import { SelectChangeEvent, useMediaQuery, useTheme } from '@mui/material';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { ApproverRole, CountZoneDto, Item, ItemModel, PrimaryCategoryStockCount, StockCheckCategory, StockCheckItem, StockCheckLocation, StockCheckTypesResponse, User, CountZoneTypeResponse, StockCheckCountZoneType } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { IStockCheckScheduleCategory, IStockCheckItem, StockCheck } from '../interfaces/StockInterfaces';
import { useNavigate, useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../hooks/useLogError';
import { ConfigurationName } from '../../../enums/ConfigurationName';
import dayjs from 'dayjs';
import { hasUnassignedZone } from '../../../utils';
import useShiftConfiguration from '../../../hooks/configurations/useShiftConfiguration';
import { ApproverType } from '../../../enums/ApproverType';
import { StockAdjustmentTiming } from '../../../enums/StockAdjustmentTiming';

export const useStockCountCreate = () => {
  //use hooks
  const { t } = useTranslation('createStockCount');
  const { selectedStore } = useContext(UserContext);
  const { getConfiguration, getStoreUsersWithRole, createStockCounts, getCountZoneDetails, getStockCheckTypes, getApproverRoles } = useNswagClient();
  const navigate = useNavigate();
  const { logError } = useLogError();
  const theme = useTheme();
  const { loadOperationalDay } = useShiftConfiguration();

  //consts
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [today, setToday] = useState<string>();


  //modal open state
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isProductTypeModalOpen, setIsProductTypeModalOpen] = useState(false);
  const [isItemsModalOpen, setIsItemsModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCategoryDeleteModalOpen, setIsCategoryDeleteModalOpen] = useState(false);
  const [isProductTypeDeleteModalOpen, setIsProductTypeDeleteModalOpen] = useState(false);
  const [isCountZonesModalOpen, setIsCountZonesModalOpen] = useState(false);
  const [isCountZonesDeleteModalOpen, setIsCountZonesDeleteModalOpen] = useState(false);
  const [isCountZonesWithItemsModalOpen, setisCountZonesWithItemsModalOpen] = useState(false);
  const [isCountZoneTypesModalOpen, setIsCountZoneTypesModalOpen] = useState(false);
  const [isCountZoneTypesDeleteModalOpen, setIsCountZoneTypesDeleteModalOpen] = useState(false);
  const [isEndOfMonthCountModalOpen, setIsEndOfMonthCountModalOpen] = useState(false);
  const [existingStockCheckStatus, setExistingStockCheckStatus] = useState<number | null>(null);
  const [existingStockCheckId, setExistingStockCheckId] = useState<number | null>(null);

  //snackbar modal state
  const [snackBarDuration, setSnackBarDuration] = useState<number | null>(6000);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackBarSeverity, setSnackBarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');

  //other state
  const [categoryData, setCategoryData] = useState<PrimaryCategoryStockCount[]>([]);
  const [productTypeData, setProductTypeData] = useState<string[]>([]);
  const [itemsData, setItemsData] = useState<Item[]>([]);
  const [categoryId, setCategoryId] = useState<number>();
  const [productType, setProductType] = useState<string>('');
  const [itemNumber, setItemNumber] = useState<number>();
  const [countZoneId, setCountZoneId] = useState<number>();
  const [countZoneTypeId, setCountZoneTypeId] = useState<number>();
  const [convertedItems, setConvertedItems] = useState<StockCheckItem[]>([]);
  const [convertedCategories, setConvertedCategories] = useState<StockCheckCategory[]>([]);
  const [countZonesData, setCountZonesData] = useState<CountZoneDto[]>([]);
  const [countZoneTypesData, setCountZoneTypesData] = useState<CountZoneTypeResponse[]>([]);
  const [convertedCountZones, setConvertedCountZones] = useState<StockCheckLocation[]>([]);
  const [convertedCountZoneTypes, setConvertedCountZoneTypes] = useState<StockCheckCountZoneType[]>([]);
  const [hasCreated, setHasCreated] = useState(false);
  const [countZoneItemsData, setcountZoneItemsData] = useState<ItemModel[]>([]);
  const [strictEmailApprovalEnabled, setStrictEmailApprovalEnabled] = useState<boolean | null>(null);
  const [autoApprovalEnabled, setAutoApprovalEnabled] = useState<boolean>(false);

  //main box values
  const [name, setName] = useState('');
  const [selectedDate, setSelectedDate] = useState<string>();
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [approverType, setApproverType] = useState<ApproverType>(ApproverType.Individual);
  const [approverRoles, setApproverRoles] = useState<ApproverRole[]>([]);
  const [selectedApproverRoleId, setSelectedApproverRoleId] = useState<number | undefined>();
  const [approvalValue, setApprovalValue] = useState<number>(0);
  const [selectedTime, setSelectedTime] = useState<number>();
  const [isTimeLocked, setIsTimeLocked] = useState<boolean>(false);
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [isSmartCount, setIsSmartCount] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [isCountZoneConfigEnabled, setisCountZoneConfigEnabled] = useState(false);
  const [stockCheckTypes, setStockCheckTypes] = useState<StockCheckTypesResponse[]>([]);
  const [selectedStockCheckType, setSelectedStockCheckType] = useState<StockCheckTypesResponse | null>(null);
  const [isToleranceSliderToggled, setIsToleranceSliderToggled] = useState<boolean>(true);
  const [adjustmentTiming, setAdjustmentTiming] = useState<StockAdjustmentTiming>(StockAdjustmentTiming.OnCompletion);


  const hasItems = itemsData.length > 0;
  const hasCategories = categoryData.length > 0;
  const hasCountZones = countZonesData.length > 0;
  const hasCountZoneTypes = countZoneTypesData.length > 0;

  const blocker = useBlocker(() => (categoryData.length > 0 || itemsData.length > 0 || productTypeData.length > 0 || hasCountZones || hasCountZoneTypes) && !hasCreated);

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  useEffect(() => {
    getUsers();
    getCurrencyCode();
    getCountZonesWithItemsConfig();
    fetchStockCheckTypes();
    fetchApproverRoles();
    const fetchStrictEmailApproval = async () => {
      if (!selectedStore?.storeNumber || !selectedStore?.franchiseName) {
        setStrictEmailApprovalEnabled(null);
        return;
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await getConfiguration(ConfigurationName.StockCountStrictEmailApproval, selectedStore.storeNumber, selectedStore.franchiseName);
        let configValue: string | undefined = undefined;
        if (response?.data) {
          configValue = response.data.value;
        }
        setStrictEmailApprovalEnabled(configValue === 'true');
      } catch {
        setStrictEmailApprovalEnabled(false);
      }
    };
    fetchStrictEmailApproval();

    const fetchAutoApprovalConfig = async () => {
      if (!selectedStore?.storeNumber || !selectedStore?.franchiseName) {
        setAutoApprovalEnabled(false);
        return;
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await getConfiguration(ConfigurationName.StockCountAutoApproval, selectedStore.storeNumber, selectedStore.franchiseName);
        let configValue: string | undefined = undefined;
        if (response?.data) {
          configValue = response.data.value;
        }
        setAutoApprovalEnabled(configValue === 'true');
      } catch {
        setAutoApprovalEnabled(false);
      }
    };
    fetchAutoApprovalConfig();
  }, []);

  useEffect(() => {
    convertAndSetStockCheckItems();
    convertAndSetStockCheckCategories();
    convertAndSetStockCheckCountZones();
    convertAndSetStockCheckCountZoneTypes();
  }, [categoryData, itemsData, countZonesData, countZoneTypesData]);

  useEffect(() => {
    if (selectedStore?.storeNumber && selectedStore?.franchiseName) {

      loadOperationalDay(selectedStore.storeNumber, selectedStore.franchiseName)
        .then((opRange) => {
          if (opRange?.start) {
            const formatted = dayjs(opRange.start).format('YYYY-MM-DD');

            setToday(formatted);
            setSelectedDate(formatted);
          }
          else {
            setToday(dayjs().format('YYYY-MM-DD'));
          }
        })
        .catch((err) => {
          console.error('Unexpected error:', err);
          throw err;
        });
    }
  }, [selectedStore]);




  const getCurrencyCode = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getConfiguration(ConfigurationName.CurrencyCode, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '').then((res: any) => {
      const code = new Intl.NumberFormat('en', { style: 'currency', currency: res.data?.value }).formatToParts(1)[0].value;
      setCurrencySymbol(code);
    });
  };

  const getCountZonesWithItemsConfig = () => {
    getConfiguration(ConfigurationName.CountZonesWithItemsEnabled, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((config: any) => {
        if (config.data.value === 'true') {
          setisCountZoneConfigEnabled(true);
        }
      })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((e: any) => logError(e));
  };

  const fetchApproverRoles = () => {
    if (!selectedStore?.storeNumber) return;
    // Fetch approver roles from the stock check schedule endpoint (which includes approverRoles)
    // This is a temporary workaround - ideally there should be a dedicated endpoint for approver roles
    getApproverRoles(selectedStore.storeNumber)
      .then((result) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = result.data;
        if (result.data) {
          setApproverRoles(result.data ?? []);
        }
      })
      .catch((error) => {
        logError(error);
      });
  };
  
  const handleSmartCountChange = (event: SelectChangeEvent) => {
    setIsSmartCount(!isSmartCount);
  };

  const handleIsTimeLockedChanged = (event: SelectChangeEvent) => {
    setIsTimeLocked(!isTimeLocked);
  };

  const STOCK_COUNT_ADMIN_ROLE = 24;
  const STOCK_COUNT_APPROVE_ROLE = 58;

  const getUsers = () => {
    if (!selectedStore?.storeNumber) return;

    Promise.all([
      getStoreUsersWithRole(STOCK_COUNT_ADMIN_ROLE, selectedStore.storeNumber),
      getStoreUsersWithRole(STOCK_COUNT_APPROVE_ROLE, selectedStore.storeNumber),
    ])
      .then(([adminRes, approveRes]) => {
        const adminUsers = adminRes?.data || [];
        const approveUsers = approveRes?.data || [];

        const mergedUsers = [...adminUsers, ...approveUsers].filter(
          (user, index, self) =>
            user && self.findIndex(u => u.id === user.id) === index,
        );
        setAllUsers(mergedUsers);
      })
      .catch((error) => {
        logError(error);
      });
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setSliderValue(newValue);
    }
  };

  const handleCategories = (data: PrimaryCategoryStockCount[]) => {
    setCategoryData(data);
    setIsCategoriesModalOpen(false);
  };

  const deleteCategories = (data: PrimaryCategoryStockCount[]) => {
    if (data.length > 0) {
      setCategoryData(data);
    }
    setCategoryId(undefined);
    setCategoryData(data);
    setIsCategoryDeleteModalOpen(false);
  };

  const handleProductTypes = (data: string[]) => {
    setProductTypeData(data);
    setIsProductTypeModalOpen(false);
  };

  const deleteProductTypes = (data: string[]) => {
    setProductType('');
    setProductTypeData(data);
    setIsProductTypeDeleteModalOpen(false);
  };

  const deleteItems = (data: Item[]) => {
    if (data.length > 0) {
      setItemsData(data);
    }
    setItemNumber(undefined);
    setItemsData(data);
    setIsDeleteModalOpen(false);
  };

  const deleteCategoryRow = (id: number) => {
    setCategoryId(id);
    setIsCategoryDeleteModalOpen(true);
  };

  const deleteProductTypeRow = (productType: string) => {
    setProductType(productType);
    setIsProductTypeDeleteModalOpen(true);
  };

  const deleteItemRow = (id: number) => {
    setItemNumber(id);
    setIsDeleteModalOpen(true);
  };

  const deleteCountZoneRow = (id: number) => {
    setCountZoneId(id);
    setIsCountZonesDeleteModalOpen(true);
  };

  const deleteCountZoneTypeRow = (id: number) => {
    setCountZoneTypeId(id);
    setIsCountZoneTypesDeleteModalOpen(true);
  };

  const deleteCountZones = (data: CountZoneDto[]) => {
    if (data.length > 0) {
      setCountZonesData(data);
    }
    setCountZoneId(undefined);
    setCountZonesData(data);
    if (!hasUnassignedZone(data)) {
      setItemsData([]);
    }
    setIsCountZonesDeleteModalOpen(false);
  };

  const handleItems = (data: Item[], ticked: boolean) => {
    const reducedItemData = itemsData.filter(x => !data.some(y => { return y.itemNumber === x.itemNumber; }));
    const items = Array.from(new Set(data.concat(reducedItemData)));
    setItemsData(items);
    setIsItemsModalOpen(ticked);
  };

  const handleBulkItems = (data: Item[]) => {
    const reducedItemData = itemsData.filter(x => !data.some(y => { return y.itemNumber === x.itemNumber; }));
    const items = Array.from(new Set(data.concat(reducedItemData)));
    setItemsData(items);
  };

  const handleClose = () => {
    setIsCategoriesModalOpen(false);
    setIsItemsModalOpen(false);
    setIsBulkModalOpen(false);
    setIsCategoryDeleteModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsProductTypeModalOpen(false);
    setIsProductTypeDeleteModalOpen(false);
    setIsCountZonesModalOpen(false);
    setIsCountZonesDeleteModalOpen(false);
    setisCountZonesWithItemsModalOpen(false);
    setIsCountZoneTypesModalOpen(false);
    setIsCountZoneTypesDeleteModalOpen(false);
    setIsEndOfMonthCountModalOpen(false);
  };

  const handleClickOpen = () => {
    setIsCategoriesModalOpen(true);
  };

  const handleProductTypeClickOpen = () => {
    setIsProductTypeModalOpen(true);
  };

  const deleteCategory = () => {
    setIsCategoryDeleteModalOpen(true);
  };

  const deleteProductType = () => {
    setIsProductTypeDeleteModalOpen(true);
  };

  const deleteCountZone = () => {
    setIsCountZonesDeleteModalOpen(true);
  };

  const deleteCountZoneType = () => {
    setIsCountZoneTypesDeleteModalOpen(true);
  };

  const deleteItem = () => {
    setIsDeleteModalOpen(true);
  };

  const handleClick = () => {
    setIsItemsModalOpen(true);
  };

  const handleClickBulkClick = () => {
    setIsBulkModalOpen(true);
  };

  const handleAddCountZonesClick = () => {
    setIsCountZonesModalOpen(true);
  };

  const handleAddCountZoneTypesClick = () => {
    setIsCountZoneTypesModalOpen(true);
  };

  const handleCountZones = (data: CountZoneDto[]) => {
    setCountZonesData(data);
    if (data.length > 0) {
      const allHaveItems = data.every(z => (z.numberOfItems ?? 0) > 0);

      if (allHaveItems) {
        setProductTypeData([]);
        setCategoryData([]);
      }
      if (!hasUnassignedZone(data)) {
        setItemsData([]);
      }
    }
    setIsCountZonesModalOpen(false);
  };

  const handleCountZoneTypes = (data: CountZoneTypeResponse[]) => {
    const uniqueTypes = Array.from(
      new Map(
        [...countZoneTypesData, ...data].map(item => [item.id, item]),
      ).values(),
    );
    setCountZoneTypesData(uniqueTypes);
    setIsCountZoneTypesModalOpen(false);
  };

  const deleteCountZoneTypes = (data: CountZoneTypeResponse[]) => {
    setCountZoneTypesData(data);
    setIsCountZoneTypesDeleteModalOpen(false);
    setCountZoneTypeId(undefined);
  }; 

  const convertAndSetStockCheckItems = () => {
    const convertedStockCheckItems = itemsData.map((item: Item) => {
      const stockCheckItem: IStockCheckItem = {
        id: item.id ?? 0,
        stockCheckId: 0,
        itemNumber: item.itemNumber ?? '',
        description: item.description ?? '',
        category: item.primaryCategory ?? '',
        productType: item.productType ?? '',
        primaryUom: item.uom ?? '',
        tolerancePercentage: 0,
        toleranceValue: 0,
        quantityOnHand: 0,
        costPriceOnHand: item.costPrice ?? 0,
      };
      return stockCheckItem;
    });
    const items = convertedStockCheckItems;
    setConvertedItems(items);
  };

  const convertAndSetStockCheckCategories = () => {
    const stockCheckCategories = categoryData.map((category: PrimaryCategoryStockCount) => {
      const stockCheckCategory: IStockCheckScheduleCategory = {
        primaryCategoryId: category.id ?? 0,
        primaryCategoryName: category.primaryCategoryName ?? '',
      };
      return stockCheckCategory;
    });
    const categories = stockCheckCategories;
    setConvertedCategories(categories);
  };

  const convertAndSetStockCheckCountZones = () => {
    const stockCheckCountZones = countZonesData.map((zone: CountZoneDto): StockCheckLocation => ({
      locationId: zone.id ?? 0,
    }));

    setConvertedCountZones(stockCheckCountZones);
  };

  const convertAndSetStockCheckCountZoneTypes = () => {
    const stockCheckCountZoneTypes = countZoneTypesData.map((type: CountZoneTypeResponse): StockCheckCountZoneType => ({
      id: type.id ?? 0,
      stockCheckId: 0,
    }));

    setConvertedCountZoneTypes(stockCheckCountZoneTypes);
  };

  const submitStock =  () => {
    const body : StockCheck = {
      id: 0,
      name: name,
      storeNumber: selectedStore?.storeNumber,
      status: 3,
      isSmartCount: isSmartCount,
      plannedCountDate: selectedDate,
      startDate: '',
      lockTillCountDay: isTimeLocked,
      lockTillHour: selectedTime,
      approver: approverType === ApproverType.Individual ? selectedUser : undefined,
      approverType: approverType,
      approverRoleId: approverType === ApproverType.Role ? selectedApproverRoleId : undefined,
      tolerancePercentage: sliderValue,
      toleranceValue: approvalValue,
      updateSmartCountItems: false,
      canMergeCount: false,
      canStartCount: false,
      documentDetailsId: undefined,
      unlockTimeUtc: '',
      parentId: undefined,
      createdBy: '',
      endDate: '',
      approvedBy: '',
      approvedDate: '',
      countedBy: '',
      items: convertedItems,
      categories: convertedCategories,
      productTypes: productTypeData,
      locations: convertedCountZones,
      stockCheckType: selectedStockCheckType?.id ?? undefined,
      countZoneTypes: convertedCountZoneTypes,
      adjustmentTiming: adjustmentTiming,
    };

    setHasCreated(true);
    setIsSubmitting(true);
    setSnackBarProps('info', 'Saving Stock Count');
    createStockCounts(body)
      .then(() => {
        setSnackBarProps('success', 'Stock Count Saved');
        navigate('/store/stock-count');
      })
      .catch((error) => {
        if (error?.status === 409) {
          const parsedData = JSON.parse(error.response);
          const { status, existingCountId } = parsedData;

          setIsEndOfMonthCountModalOpen(true);
          setExistingStockCheckStatus(status);
          setExistingStockCheckId(existingCountId);
          return;
        }
        setSnackBarProps('error', `${t('errorMessage')} ${error}`);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleInputValue = (event: ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseInt(event.target.value, 10);
    if (!isNaN(newQuantity)) {
      setApprovalValue(newQuantity);
    } else {
      setApprovalValue(0);
    }
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      const formattedTime = hour;
      options.push({
        value: formattedTime,
        label: `${String(hour).padStart(2, '0')}:00`,
      });
    }
    return options;
  };

  const handleSelectChange = (event: SelectChangeEvent<number>) => {
    setSelectedTime(event.target.value as number | undefined);
  };

  const handleUserSelect = (event: SelectChangeEvent<string>) => {
    setSelectedUser(event.target.value);
  };

  const handleApproverTypeChange = (event: SelectChangeEvent<number>) => {
    const newType = event.target.value as ApproverType;
    setApproverType(newType);
    // Reset the other selection when switching types
    if (newType === ApproverType.Individual) {
      setSelectedApproverRoleId(undefined);
    } else if (newType === ApproverType.AutoApproval) {
      setSelectedUser('');
      setSelectedApproverRoleId(undefined);
    } else {
      setSelectedUser('');
    }
  };

  const handleApproverRoleSelect = (event: SelectChangeEvent<number>) => {
    setSelectedApproverRoleId(event.target.value as number);
  };

  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  const handleLinkClick = () => {
    navigate('/store/stock-count');
  };

  const handleValueChange = (id: number, newValue: string) => {
    let newNum = Number(newValue) || 0;
    newNum = newNum < 0 ? 0 : newNum;

    const updatedItems = convertedItems.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          toleranceValue: newNum,
        };
      }
      return item;
    });
    setConvertedItems(updatedItems);
  };

  const handleQtyChange = (id: number, newValue: string) => {
    let newNum = Number(newValue) || 0;
    newNum = newNum < 0 ? 0 : newNum;

    const updatedItems = convertedItems.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          tolerancePercentage: newNum,
        };
      }
      return item;
    });
    setConvertedItems(updatedItems);
  };

  const handleViewCountZoneItemsClick = (countZoneId: number) => {

    getCountZoneDetails(countZoneId)
      .then(response => {
        const data = response.data;
        if (!data) return;

        setcountZoneItemsData(data.items ?? []);
        setisCountZonesWithItemsModalOpen(true);
      })
      .catch(error => {
        logError(error);
      });
  };

  const setSnackBarProps = (severity: 'error' | 'warning' | 'info' | 'success', message: string) => {
    severity === 'success' ? setSnackBarDuration(6000) : setSnackBarDuration(null);
    setIsSnackBarOpen(true);
    setSnackBarSeverity(severity);
    setSnackbarMessage(message);
  };

  const closeSnackBar = () => {
    setIsSnackBarOpen(false);
  };

  const fetchStockCheckTypes = () => {
    getStockCheckTypes()
      .then((res) => {
        setStockCheckTypes(res.data ?? []);
      })
      .catch((e) => logError(e));
  };

  const handleStockCheckTypeChange = (event: SelectChangeEvent<number>) => {
    const id = Number(event.target.value);
    setSelectedStockCheckType(stockCheckTypes.find(type => type.id === id) || null);
  };

  const handleIsToleranceSliderToggledChange = (event: SelectChangeEvent) => {
    const newToggleState = !isToleranceSliderToggled;
    setIsToleranceSliderToggled(newToggleState);

    if (newToggleState) {
      setApprovalValue(0);
    } else {
      setSliderValue(0);
    }
  };

  const handleAdjustmentTimingChange = (event: SelectChangeEvent<number>) => {
    setAdjustmentTiming(Number(event.target.value) as StockAdjustmentTiming);
  };

  return {
    t,
    isCategoriesModalOpen,
    isProductTypeModalOpen,
    isItemsModalOpen,
    isBulkModalOpen,
    hasCategories,
    isCategoryDeleteModalOpen,
    isProductTypeDeleteModalOpen,
    isDeleteModalOpen,
    isEndOfMonthCountModalOpen,
    existingStockCheckId,
    existingStockCheckStatus,
    categoryId,
    productType,
    itemNumber,
    hasItems,
    isMobile,
    allUsers,
    handleSmartCountChange,
    handleQtyChange,
    handleValueChange,
    handleLinkClick,
    handleDateChange,
    handleUserSelect,
    handleSelectChange,
    generateTimeOptions,
    handleInputValue,
    handleInputChange,
    submitStock,
    handleClickBulkClick,
    handleClick,
    deleteItem,
    deleteCategory,
    deleteProductType,
    handleClickOpen,
    handleProductTypeClickOpen,
    handleClose,
    handleBulkItems,
    handleItems,
    deleteItemRow,
    deleteCategoryRow,
    deleteProductTypeRow,
    deleteItems,
    deleteCategories,
    deleteProductTypes,
    handleCategories,
    handleProductTypes,
    handleSliderChange,
    handleIsTimeLockedChanged,
    setIsEndOfMonthCountModalOpen,
    name,
    selectedDate,
    today,
    selectedUser,
    approverType,
    approverRoles,
    selectedApproverRoleId,
    handleApproverTypeChange,
    handleApproverRoleSelect,
    approvalValue,
    selectedTime,
    isTimeLocked,
    sliderValue,
    categoryData,
    productTypeData,
    convertedItems,
    itemsData,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    isSmartCount,
    blocker,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    isSubmitting,
    currencySymbol,
    isCountZoneConfigEnabled,
    handleAddCountZonesClick,
    isCountZonesModalOpen,
    isCountZonesDeleteModalOpen,
    handleCountZones,
    countZonesData,
    convertedCountZones,
    deleteCountZoneRow,
    deleteCountZone,
    deleteCountZones,
    countZoneId,
    handleViewCountZoneItemsClick,
    isCountZonesWithItemsModalOpen,
    countZoneItemsData,
    hasCountZones,
    isCountZoneTypesModalOpen,
    isCountZoneTypesDeleteModalOpen,
    countZoneTypesData,
    countZoneTypeId,
    hasCountZoneTypes,
    handleAddCountZoneTypesClick,
    handleCountZoneTypes,
    deleteCountZoneTypeRow,
    deleteCountZoneType,
    deleteCountZoneTypes,
    handleStockCheckTypeChange,
    stockCheckTypes,
    selectedStockCheckType,
    strictEmailApprovalEnabled,
    autoApprovalEnabled,
    isToleranceSliderToggled,
    handleIsToleranceSliderToggledChange,
    adjustmentTiming,
    handleAdjustmentTimingChange,
  };
};